import React, { useCallback, useEffect, useRef } from 'react';
import { useIntakeFormStore } from '../../stores/intakeFormStore';
import { useThemeClasses } from '../../lib/themes/utils';

export interface FormFieldProps {
  // Field configuration
  path: string;
  label?: string;
  subLabel?: string;
  type?: 'text' | 'email' | 'tel' | 'number' | 'date' | 'select' | 'textarea' | 'checkbox' | 'radio' | 'slider' | 'custom';
  required?: boolean;
  placeholder?: string;
  helperText?: string;
  
  // Select/Radio options
  options?: Array<{ value: string; label: string }>;
  
  // Number/Slider props
  min?: number;
  max?: number;
  step?: number;
  
  // Textarea props
  rows?: number;
  
  // Validation
  validate?: (value: any) => string | undefined;
  
  // Custom render prop for complex fields
  render?: (props: {
    value: any;
    onChange: (value: any) => void;
    onBlur: () => void;
    error?: string;
    theme: ReturnType<typeof useThemeClasses>;
  }) => React.ReactNode;
  
  // Styling
  className?: string;
  inputClassName?: string;
  labelClassName?: string;
  
  // Other props
  disabled?: boolean;
  autoFocus?: boolean;
  onBlur?: () => void;
  onChange?: (value: any) => void;
}

export const FormField: React.FC<FormFieldProps> = ({
  path,
  label,
  subLabel,
  type = 'text',
  required = false,
  placeholder,
  helperText,
  options = [],
  min,
  max,
  step,
  rows = 3,
  validate,
  render,
  className = '',
  inputClassName = '',
  labelClassName = '',
  disabled = false,
  autoFocus = false,
  onBlur,
  onChange
}) => {
  const theme = useThemeClasses();
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(null);
  
  // Get store actions and state
  const updateField = useIntakeFormStore(state => state.updateField);
  const getFieldValue = useIntakeFormStore(state => state.getFieldValue);
  const errors = useIntakeFormStore(state => state.errors);
  
  // Get current value from store
  const value = getFieldValue(path) || '';
  const error = errors[path];
  
  // Handle field change
  const handleChange = useCallback((newValue: any) => {
    updateField(path, newValue);
    
    // Call custom onChange if provided
    if (onChange) {
      onChange(newValue);
    }
    
    // Run validation if provided
    if (validate) {
      const validationError = validate(newValue);
      if (validationError) {
        useIntakeFormStore.getState().setErrors({
          ...useIntakeFormStore.getState().errors,
          [path]: validationError
        });
      } else {
        // Clear error if validation passes
        const newErrors = { ...useIntakeFormStore.getState().errors };
        delete newErrors[path];
        useIntakeFormStore.getState().setErrors(newErrors);
      }
    }
  }, [path, updateField, onChange, validate]);
  
  // Handle blur event
  const handleBlur = useCallback(() => {
    if (onBlur) {
      onBlur();
    }
    
    // Run validation on blur if provided
    if (validate) {
      const validationError = validate(value);
      if (validationError) {
        useIntakeFormStore.getState().setErrors({
          ...useIntakeFormStore.getState().errors,
          [path]: validationError
        });
      }
    }
  }, [path, value, validate, onBlur]);
  
  // Auto-focus effect
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);
  
  // Base input classes
  const baseInputClasses = `
    w-full px-4 py-2 ${theme.radius.medium} ${theme.borders.neutral}
    bg-white ${theme.text.neutral}
    focus:outline-none focus:ring-2 focus:ring-primary-400
    disabled:opacity-50 disabled:cursor-not-allowed
    transition-colors ${theme.animations.duration}
    ${error ? 'border-red-500 focus:ring-red-200' : ''}
    ${inputClassName}
  `;
  
  // Custom render
  if (render) {
    return (
      <div className={`mb-4 ${className}`}>
        {label && (
          <label className={`block mb-2 ${theme.text.neutral} font-medium ${labelClassName}`}>
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
            {subLabel && <span className={`block text-sm ${theme.text.neutralLight} font-normal mt-1`}>{subLabel}</span>}
          </label>
        )}
        {render({
          value,
          onChange: handleChange,
          onBlur: handleBlur,
          error,
          theme
        })}
        {helperText && !error && (
          <p className={`mt-1 text-sm ${theme.text.neutralLight}`}>{helperText}</p>
        )}
        {error && (
          <p className={`mt-1 text-sm text-red-600`}>{error}</p>
        )}
      </div>
    );
  }
  
  // Standard field render
  return (
    <div className={`mb-4 ${className}`}>
      {label && (
        <label 
          htmlFor={path}
          className={`block mb-2 ${theme.text.neutral} font-medium ${labelClassName}`}
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
          {subLabel && <span className={`block text-sm ${theme.text.neutralLight} font-normal mt-1`}>{subLabel}</span>}
        </label>
      )}
      
      {/* Text inputs */}
      {['text', 'email', 'tel', 'number', 'date'].includes(type) && (
        <input
          ref={inputRef as React.RefObject<HTMLInputElement>}
          id={path}
          type={type}
          value={value}
          onChange={(e) => handleChange(type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value)}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          min={min}
          max={max}
          step={step}
          className={baseInputClasses}
        />
      )}
      
      {/* Select */}
      {type === 'select' && (
        <select
          ref={inputRef as React.RefObject<HTMLSelectElement>}
          id={path}
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={handleBlur}
          disabled={disabled}
          required={required}
          className={baseInputClasses}
        >
          <option value="">{placeholder || 'Select an option'}</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      )}
      
      {/* Textarea */}
      {type === 'textarea' && (
        <textarea
          ref={inputRef as React.RefObject<HTMLTextAreaElement>}
          id={path}
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          rows={rows}
          className={`${baseInputClasses} resize-vertical`}
        />
      )}
      
      {/* Checkbox */}
      {type === 'checkbox' && (
        <div className="flex items-center">
          <input
            id={path}
            type="checkbox"
            checked={value === true}
            onChange={(e) => handleChange(e.target.checked)}
            onBlur={handleBlur}
            disabled={disabled}
            required={required}
            className={`
              w-5 h-5 ${theme.radius.small} ${theme.borders.neutral}
              text-primary-600 focus:ring-2 focus:ring-primary-400
              disabled:opacity-50 disabled:cursor-not-allowed
            `}
          />
          {label && (
            <label 
              htmlFor={path}
              className={`ml-2 ${theme.text.neutral} ${labelClassName}`}
            >
              {label}
              {required && <span className="text-red-500 ml-1">*</span>}
            </label>
          )}
        </div>
      )}
      
      {/* Radio group */}
      {type === 'radio' && (
        <div className="space-y-2">
          {options.map((option) => (
            <div key={option.value} className="flex items-center">
              <input
                id={`${path}-${option.value}`}
                type="radio"
                name={path}
                value={option.value}
                checked={value === option.value}
                onChange={() => handleChange(option.value)}
                onBlur={handleBlur}
                disabled={disabled}
                required={required}
                className={`
                  w-5 h-5 ${theme.borders.neutral}
                  text-primary-600 focus:ring-2 focus:ring-primary-400
                  disabled:opacity-50 disabled:cursor-not-allowed
                `}
              />
              <label 
                htmlFor={`${path}-${option.value}`}
                className={`ml-2 ${theme.text.neutral}`}
              >
                {option.label}
              </label>
            </div>
          ))}
        </div>
      )}
      
      {/* Slider */}
      {type === 'slider' && (
        <div>
          <input
            id={path}
            type="range"
            value={value || min || 0}
            onChange={(e) => handleChange(parseFloat(e.target.value))}
            onBlur={handleBlur}
            disabled={disabled}
            required={required}
            min={min || 0}
            max={max || 100}
            step={step || 1}
            className={`
              w-full h-2 ${theme.radius.full}
              bg-neutral-200 appearance-none cursor-pointer
              disabled:opacity-50 disabled:cursor-not-allowed
              [&::-webkit-slider-thumb]:appearance-none
              [&::-webkit-slider-thumb]:w-4
              [&::-webkit-slider-thumb]:h-4
              [&::-webkit-slider-thumb]:${theme.radius.full}
              [&::-webkit-slider-thumb]:bg-primary-600
              [&::-webkit-slider-thumb]:cursor-pointer
              [&::-webkit-slider-thumb]:transition-transform
              [&::-webkit-slider-thumb]:hover:scale-110
              [&::-moz-range-thumb]:w-4
              [&::-moz-range-thumb]:h-4
              [&::-moz-range-thumb]:${theme.radius.full}
              [&::-moz-range-thumb]:bg-primary-600
              [&::-moz-range-thumb]:border-0
              [&::-moz-range-thumb]:cursor-pointer
              [&::-moz-range-thumb]:transition-transform
              [&::-moz-range-thumb]:hover:scale-110
            `}
          />
          <div className="flex justify-between mt-1">
            <span className={`text-sm ${theme.text.neutralLight}`}>{min || 0}</span>
            <span className={`text-sm ${theme.text.neutral} font-medium`}>{value || min || 0}</span>
            <span className={`text-sm ${theme.text.neutralLight}`}>{max || 100}</span>
          </div>
        </div>
      )}
      
      {/* Helper text */}
      {helperText && !error && (
        <p className={`mt-1 text-sm ${theme.text.neutralLight}`}>{helperText}</p>
      )}
      
      {/* Error message */}
      {error && (
        <p className={`mt-1 text-sm text-red-600`} role="alert">{error}</p>
      )}
    </div>
  );
};

// Export helper components for common field types
export const TextField: React.FC<Omit<FormFieldProps, 'type'>> = (props) => (
  <FormField {...props} type="text" />
);

export const EmailField: React.FC<Omit<FormFieldProps, 'type'>> = (props) => (
  <FormField {...props} type="email" />
);

export const PhoneField: React.FC<Omit<FormFieldProps, 'type'>> = (props) => (
  <FormField {...props} type="tel" />
);

export const NumberField: React.FC<Omit<FormFieldProps, 'type'>> = (props) => (
  <FormField {...props} type="number" />
);

export const DateField: React.FC<Omit<FormFieldProps, 'type'>> = (props) => (
  <FormField {...props} type="date" />
);

export const SelectField: React.FC<Omit<FormFieldProps, 'type'>> = (props) => (
  <FormField {...props} type="select" />
);

export const TextAreaField: React.FC<Omit<FormFieldProps, 'type'>> = (props) => (
  <FormField {...props} type="textarea" />
);

export const CheckboxField: React.FC<Omit<FormFieldProps, 'type'>> = (props) => (
  <FormField {...props} type="checkbox" />
);

export const RadioField: React.FC<Omit<FormFieldProps, 'type'>> = (props) => (
  <FormField {...props} type="radio" />
);

export const SliderField: React.FC<Omit<FormFieldProps, 'type'>> = (props) => (
  <FormField {...props} type="slider" />
);